import * as z from 'zod'

import type ApiService from '@nxt-insurance/api-service'
import { HTTP_METHOD } from '@nxt-insurance/api-service'
import {
  NAME_LENGTH_REGEX,
  NO_INVALID_CHARACTER_USERNAME_REGEX,
  SupportedMarketKey,
} from '@nxt-insurance/constants'
import { phoneNumber as phoneNumberUtils } from '@nxt-insurance/helpers'

import type { SchemaPath } from '@flows/configs/shared/schemaUtils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { t } from '@flows/core/i18n'
import type { CheckboxOption } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import { getTransientPath } from '@flows/insurance/utils'

export const MARKETING_CONSENT_KEY_TO_MARKET_MAP = {
  [SupportedMarketKey.De]: 'emailMarketingConsentDe',
  [SupportedMarketKey.At]: 'emailMarketingConsentAt',
}

export const MARKETING_CONSENT_FORM_KEY_TO_MARKET_MAP = {
  [SupportedMarketKey.De]: 'marketingOptIn',
  [SupportedMarketKey.At]: 'electronic_communication',
}

export const checkboxConfirmationsMarket: Record<SupportedMarketKey, CheckboxOption[]> = {
  [SupportedMarketKey.De]: [
    {
      name: MARKETING_CONSENT_FORM_KEY_TO_MARKET_MAP[SupportedMarketKey.De],
      title: {
        type: 'nonTranslatableText',
        text: '{{consentText}}',
        values: {
          consentText: {
            type: 'dynamicValue',
            path: getTransientPath('marketing_consent_text'),
          },
        },
      },
      isOptional: true,
    },
  ],
  [SupportedMarketKey.At]: [
    {
      name: MARKETING_CONSENT_FORM_KEY_TO_MARKET_MAP[SupportedMarketKey.At],
      title: {
        type: 'translatableText',
        translationKey:
          'sharedFlows_common:steps.email_and_name.checkboxes.electronic_communication.title',
      },
      description: {
        type: 'nonTranslatableText',
        text: '{{consentText}}',
        values: {
          consentText: {
            type: 'dynamicValue',
            path: getTransientPath('marketing_consent_text'),
          },
        },
      },
      defaultOpened: true,
      isOptional: true,
    },
  ],
}

export const getCheckboxConfirmationsMarketSchema = (): Partial<
  Record<SupportedMarketKey, Record<string, SchemaPath>>
> => ({
  [SupportedMarketKey.De]: {
    marketingOptIn: z.boolean().optional(),
  },
  [SupportedMarketKey.At]: {
    electronic_communication: z.boolean().optional(),
  },
})

interface EmailAndNameProps {
  market: SupportedMarketKey
  apiService: ApiService
}

export const getPhoneNumberSchema = () =>
  z
    .string()
    .refine(
      (value) => phoneNumberUtils.isValidNumber(value),
      t('sharedFlows_common:generic.errors.phone_invalid')
    )

const email_and_name = ({ market, apiService }: EmailAndNameProps) => {
  const phoneNumberSchema = getPhoneNumberSchema()
  return {
    schema: schemaUtils.generateZodObjectFromPaths({
      email: z.string().email(),
      phone_number: phoneNumberSchema,
      first_name: z
        .string()
        .min(1)
        .regex(
          NO_INVALID_CHARACTER_USERNAME_REGEX,
          t('sharedFlows_common:generic.errors.name_invalid_characters')
        )
        .regex(NAME_LENGTH_REGEX, t('sharedFlows_common:generic.errors.name_incorrect_length')),
      last_name: z
        .string()
        .min(1)
        .regex(
          NO_INVALID_CHARACTER_USERNAME_REGEX,
          t('sharedFlows_common:generic.errors.name_invalid_characters')
        )
        .regex(NAME_LENGTH_REGEX, t('sharedFlows_common:generic.errors.name_incorrect_length')),
      ...(getCheckboxConfirmationsMarketSchema()[market] || {}),
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.title',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey: 'sharedFlows_common:steps.email_and_name.infoButton.dataTitle',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_common:steps.email_and_name.infoButton.dataExplanation',
              },
            },
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_common:steps.email_and_name.infoButton.phoneNumberTitle',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_common:steps.email_and_name.infoButton.phoneNumberExplanation',
              },
            },
          ],
        },
      },
      {
        type: 'input',
        name: 'email',
        disabledIfValueExists: true,
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.email.input.label',
        },
      },
      {
        type: 'phoneNumber',
        name: 'phone_number',
        disabledIfValueExists: true,
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.phone.input.label',
        },
        defaultCountry: market,
      },
      {
        type: 'input',
        name: 'first_name',
        disabledIfValueExists: true,
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.firstName.input.label',
        },
      },
      {
        type: 'input',
        name: 'last_name',
        disabledIfValueExists: true,
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.lastName.input.label',
        },
      },
      {
        type: 'checkbox',
        blockTitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.email_and_name.checkboxes.title',
        },
        fields: checkboxConfirmationsMarket[market] || checkboxConfirmationsMarket.de,
      },
    ],
    onPrepare: {
      type: 'simpleHook',
      callback: async () => {
        const response = await apiService.call({
          serviceName: 'personalizationService',
          method: HTTP_METHOD.GET,
          url: `/api/v3/legal_texts/versioned?market=${market}`,
        })
        const marketingConsentText = response.data[MARKETING_CONSENT_KEY_TO_MARKET_MAP[market]]

        if (!marketingConsentText?.contentVersion.contentHtml) {
          throw new Error('Assertion: marketing consent text in undefined')
        }

        return {
          formValues: {
            [getTransientPath('marketing_consent_text')]:
              marketingConsentText.contentVersion.contentHtml,
            [getTransientPath('marketing_consent_version')]:
              marketingConsentText.contentVersion.key,
          },
        }
      },
    },
    subFlow: SubFlow.Common,
  } as const satisfies AnyStepConfiguration
}

export default email_and_name
